import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Camera, CheckCircle2, ChevronDown, ChevronUp, FileText, Globe, ImageOff,
  Loader2, Plus, RefreshCw, Rocket, Save, Sparkles, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);
const newKey = (p) => `${p}_${Math.random().toString(16).slice(2, 12)}`;

function errorText(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return detail.message;
  return fallback;
}

/** Price typed in dollars, kept as integer cents. */
function PriceInput({ cents, onChange, testId }) {
  const [text, setText] = useState(cents == null ? '' : (cents / 100).toFixed(2));
  useEffect(() => { setText(cents == null ? '' : (cents / 100).toFixed(2)); }, [cents]);
  const commit = () => {
    const t = text.replace(/[$\s,]/g, '');
    if (t === '') { onChange(null); return; }
    if (!/^\d{1,5}(\.\d{0,2})?$/.test(t)) { setText(cents == null ? '' : (cents / 100).toFixed(2)); return; }
    const [w, f = ''] = t.split('.');
    onChange(parseInt(w, 10) * 100 + parseInt((f + '00').slice(0, 2), 10));
  };
  return (
    <div className="relative w-28">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <Input
        value={text}
        inputMode="decimal"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        className={`pl-6 text-right font-semibold tabular-nums ${cents == null ? 'border-amber-500' : ''}`}
        placeholder="0.00"
        data-testid={testId}
      />
    </div>
  );
}

function sourceLabel(src) {
  if (!src) return null;
  if (src.kind === 'page') return `${src.file || 'upload'} · page ${src.page}`;
  if (src.kind === 'url') {
    const via = { jsonld: 'website menu data', html: 'website text', 'linked file': 'website menu file', file: 'linked file' }[src.via] || 'website';
    let host = '';
    try { host = new URL(src.url).hostname; } catch (e) { host = ''; }
    return `${host}${host ? ' · ' : ''}${via}${src.page ? ` · page ${src.page}` : ''}`;
  }
  return null;
}

function MatchControl({ row, decision, onDecide }) {
  if (!row || !row.match) {
    return <Badge className="bg-emerald-600/90 text-white hover:bg-emerald-600/90" data-testid="match-new">New</Badge>;
  }
  const m = row.match;
  const priceChange = (m.changes || []).find((c) => c.field === 'price');
  const opts = [
    ['update', 'Update existing'],
    ['create', 'Add as new item'],
    ['skip', 'Skip'],
  ];
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs space-y-1.5" data-testid="match-existing">
      <div className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-300">
        <AlertTriangle className="h-3.5 w-3.5" />
        {m.kind === 'possible' ? 'Looks like' : 'Already on your menu'}: {m.name}
        {m.category ? <span className="font-normal opacity-80">({m.category})</span> : null}
      </div>
      <div className="text-muted-foreground">
        {(m.changes || []).length === 0 ? 'Same as on your menu' : [
          priceChange ? <span key="p">Price {money(priceChange.from)} → <b>{money(priceChange.to)}</b></span> : null,
          (m.changes || []).some((c) => c.field === 'description') ? <span key="d">new description</span> : null,
          (m.changes || []).some((c) => c.field === 'photo') ? <span key="ph">{m.imageUrl ? 'new photo' : 'adds a photo'}</span> : null,
        ].filter(Boolean).reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
      </div>
      <div className="flex flex-wrap gap-1">
        {opts.map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => onDecide(v)}
            className={`rounded-md px-2 py-1 border transition ${decision === v
              ? 'bg-foreground text-background border-foreground'
              : 'bg-background hover:bg-muted border-border'}`}
            data-testid={`decide-${v}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ItemCard({ item, row, decision, onDecide, onChange, onRemove, onPhoto, uploading }) {
  const fileRef = useRef(null);
  const [showMods, setShowMods] = useState(false);
  const src = sourceLabel(item.source);
  return (
    <div className="group rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="draft-item">
      <div className="relative aspect-[4/3] bg-muted">
        {item.photo?.url ? (
          <img src={item.photo.url} alt={item.name} className="h-full w-full object-cover" data-testid="draft-item-photo" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageOff className="h-7 w-7 opacity-60" />
            <span className="text-xs">No photo</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1.5 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                 onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) onPhoto(f); }} />
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => fileRef.current?.click()}
                  disabled={uploading} data-testid="photo-replace">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1" />}
            {item.photo ? 'Replace' : 'Add photo'}
          </Button>
          {item.photo ? (
            <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => onChange({ photo: null })}
                    data-testid="photo-remove">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Remove
            </Button>
          ) : null}
        </div>
        {item.code ? (
          <span className="absolute left-2 top-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-semibold text-white tabular-nums">
            #{item.code}
          </span>
        ) : null}
        {item.photo?.source === 'crop' ? (
          <span className="absolute right-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white">from your menu</span>
        ) : item.photo?.source === 'web' ? (
          <span className="absolute right-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white">from your website</span>
        ) : null}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Input value={item.name} onChange={(e) => onChange({ name: e.target.value })}
                 className="font-semibold" aria-label="Item name" data-testid="draft-item-name" />
          <PriceInput cents={item.priceCents} onChange={(c) => onChange({ priceCents: c, price: c == null ? null : c / 100 })}
                      testId="draft-item-price" />
        </div>
        {item.priceCents == null ? (
          <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />No price on the menu — set one before publishing</p>
        ) : null}
        <Textarea value={item.description || ''} onChange={(e) => onChange({ description: e.target.value })}
                  rows={2} className="text-sm resize-none" placeholder="Description" data-testid="draft-item-desc" />
        {(item.modifiers || []).length > 0 ? (
          <div className="rounded-lg bg-muted/60 p-2 text-xs">
            <button type="button" className="flex w-full items-center justify-between font-medium" onClick={() => setShowMods(!showMods)}>
              <span>{item.modifiers.length} option group{item.modifiers.length > 1 ? 's' : ''}: {item.modifiers.map((m) => m.groupName).join(', ')}</span>
              {showMods ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showMods ? (
              <div className="mt-2 space-y-1.5">
                {item.modifiers.map((m, gi) => (
                  <div key={gi}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{m.groupName}{m.minSelect > 0 ? ' · required' : ''}</span>
                      <button type="button" className="text-muted-foreground hover:text-destructive"
                              onClick={() => onChange({ modifiers: item.modifiers.filter((_, i) => i !== gi) })}>remove</button>
                    </div>
                    <div className="text-muted-foreground">
                      {m.options.map((o) => `${o.name}${o.priceDeltaCents ? ` +${money(o.priceDeltaCents)}` : ''}`).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <MatchControl row={row} decision={decision} onDecide={onDecide} />
        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            {src ? (item.source?.kind === 'url' ? <Globe className="h-3 w-3" /> : <FileText className="h-3 w-3" />) : null}
            {src}
          </span>
          <button type="button" onClick={onRemove} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid="draft-item-remove">
            <Trash2 className="h-3.5 w-3.5" />Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Review the AI-built draft and publish it to the live menu.
 * Props: job (with job.draft), onPublished(result), onRefresh()
 */
export default function MenuDraftReview({ job, onPublished }) {
  const [draft, setDraft] = useState(job.draft);
  const [plan, setPlan] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => { setDraft(job.draft); setDirty(false); }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlan = async () => {
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs/${job.id}/plan`, { headers: getBranchAuthHeaders() });
      if (res.ok) {
        const p = await res.json();
        setPlan(p);
        // a dish already on the menu starts as "update" - shown, never silent; the owner can change it
        setDecisions((d) => {
          const next = { ...d };
          p.items.forEach((r) => { if (r.match && !next[r.key]) next[r.key] = 'update'; });
          return next;
        });
      }
    } catch (e) { /* plan is advisory; publish re-checks */ }
  };
  useEffect(() => { loadPlan(); }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => Object.fromEntries((plan?.items || []).map((r) => [r.key, r])), [plan]);
  const items = (draft?.categories || []).flatMap((c) => c.items);
  const stats = {
    categories: (draft?.categories || []).length,
    items: items.length,
    photos: items.filter((i) => i.photo).length,
    needsPrice: items.filter((i) => i.priceCents == null && decisions[i.key] !== 'skip').length,
    existing: items.filter((i) => rows[i.key]?.match).length,
  };

  const mutate = (fn) => { setDraft((d) => { const n = JSON.parse(JSON.stringify(d)); fn(n); return n; }); setDirty(true); };
  const updateItem = (ci, ii, patch) => mutate((d) => { Object.assign(d.categories[ci].items[ii], patch); });
  const removeItem = (ci, ii) => mutate((d) => { d.categories[ci].items.splice(ii, 1); if (!d.categories[ci].items.length) d.categories.splice(ci, 1); });
  const moveCategory = (ci, dir) => mutate((d) => { const j = ci + dir; if (j < 0 || j >= d.categories.length) return; [d.categories[ci], d.categories[j]] = [d.categories[j], d.categories[ci]]; });
  const addItem = (ci) => mutate((d) => { d.categories[ci].items.push({ key: newKey('it'), code: null, name: 'New item', description: '', priceCents: null, price: null, isAvailable: true, modifiers: [], photo: null, source: null, flags: ['needs_price'] }); });
  const addCategory = () => mutate((d) => { d.categories.push({ key: newKey('cat'), name: 'New category', suggested: false, items: [{ key: newKey('it'), code: null, name: 'New item', description: '', priceCents: null, price: null, isAvailable: true, modifiers: [], photo: null, source: null, flags: ['needs_price'] }] }); });

  const save = async (quiet = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs/${job.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBranchAuthHeaders() },
        body: JSON.stringify({ userEditedMenuJson: draft }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(errorText(body.detail, 'Could not save your changes')); return false; }
      setDraft(body.draft);
      setDirty(false);
      if (!quiet) toast.success('Draft saved');
      await loadPlan();
      return true;
    } catch (e) {
      toast.error('Could not save your changes');
      return false;
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (ci, ii, file) => {
    const key = draft.categories[ci].items[ii].key;
    if (dirty && !(await save(true))) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/menu-import/jobs/${job.id}/items/${key}/photo`, {
        method: 'POST', headers: getBranchAuthHeaders(), body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(errorText(body.detail, 'The photo could not be saved')); return; }
      setDraft((d) => {
        const n = JSON.parse(JSON.stringify(d));
        n.categories.forEach((c) => c.items.forEach((it) => { if (it.key === key) it.photo = body.photo; }));
        return n;
      });
      toast.success('Photo updated');
    } finally { setUploadingKey(null); }
  };

  const publish = async () => {
    setProblem(null);
    if (stats.needsPrice) { setProblem(`Set a price for ${stats.needsPrice} item(s) before publishing.`); return; }
    if (dirty && !(await save(true))) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs/${job.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBranchAuthHeaders() },
        body: JSON.stringify({ mode: 'MERGE', decisions }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) { await loadPlan(); setProblem(errorText(body.detail, 'Some items already exist - choose what to do with each.')); return; }
      if (!res.ok) { setProblem(errorText(body.detail, 'Publishing failed; nothing was changed.')); return; }
      onPublished && onPublished(body);
    } catch (e) {
      setProblem('Publishing failed; nothing was changed.');
    } finally { setPublishing(false); }
  };

  if (!draft) return null;

  return (
    <div className="space-y-5" data-testid="menu-draft-review">
      {/* summary */}
      <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-primary"><Sparkles className="h-4 w-4" />AI draft — review before it goes live</div>
            <h2 className="mt-1 text-2xl font-bold tracking-tight">
              {stats.items} items in {stats.categories} categories
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {stats.photos} with dish photos · prices exactly as printed · nothing is on your menu until you publish
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Stat label="Categories" value={stats.categories} />
            <Stat label="Items" value={stats.items} />
            <Stat label="Photos" value={stats.photos} />
            <Stat label="Already on menu" value={stats.existing} tone={stats.existing ? 'amber' : undefined} />
            <Stat label="Need a price" value={stats.needsPrice} tone={stats.needsPrice ? 'red' : undefined} />
          </div>
        </div>
        {(draft.notes || []).length ? (
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <div className="font-medium mb-1">Notes from the AI</div>
            <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">{draft.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          </div>
        ) : null}
      </div>

      {draft.categories.map((cat, ci) => (
        <section key={cat.key} className="space-y-3" data-testid="draft-category">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={cat.name} onChange={(e) => mutate((d) => { d.categories[ci].name = e.target.value; })}
                   className="max-w-xs text-lg font-bold h-10" aria-label="Category name" data-testid="draft-category-name" />
            {cat.suggested ? <Badge variant="outline" className="text-xs">AI-suggested section</Badge> : <Badge variant="outline" className="text-xs">section as on your menu</Badge>}
            <span className="text-sm text-muted-foreground">{cat.items.length} item{cat.items.length === 1 ? '' : 's'}</span>
            <div className="ml-auto flex gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveCategory(ci, -1)} disabled={ci === 0} aria-label="Move up"><ChevronUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveCategory(ci, 1)} disabled={ci === draft.categories.length - 1} aria-label="Move down"><ChevronDown className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => addItem(ci)}><Plus className="h-4 w-4 mr-1" />Item</Button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {cat.items.map((it, ii) => (
              <ItemCard
                key={it.key}
                item={it}
                row={rows[it.key]}
                decision={decisions[it.key]}
                onDecide={(v) => setDecisions((d) => ({ ...d, [it.key]: v }))}
                onChange={(patch) => updateItem(ci, ii, patch)}
                onRemove={() => removeItem(ci, ii)}
                onPhoto={(f) => uploadPhoto(ci, ii, f)}
                uploading={uploadingKey === it.key}
              />
            ))}
          </div>
        </section>
      ))}

      <Button variant="outline" onClick={addCategory}><Plus className="h-4 w-4 mr-1" />Add category</Button>

      {/* publish bar */}
      <div className="sticky bottom-0 z-10 -mx-1 rounded-t-2xl border bg-background/95 backdrop-blur p-4 shadow-lg">
        {problem ? (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="publish-problem">
            <AlertTriangle className="h-4 w-4 shrink-0" />{problem}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-muted-foreground mr-auto">
            {stats.existing ? `${stats.existing} item(s) already on your menu — your choice is applied to each. ` : ''}
            Publishing makes the menu live on online ordering, the kiosk and the POS.
          </div>
          <Button variant="outline" onClick={() => save(false)} disabled={saving || publishing || !dirty} data-testid="save-draft">
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {dirty ? 'Save draft' : 'Saved'}
          </Button>
          <Button onClick={publish} disabled={publishing || saving} className="min-w-[200px]" data-testid="publish-menu">
            {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
            Publish to menu
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === 'red' ? 'text-red-600 dark:text-red-400' : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="rounded-xl border bg-card px-3 py-2 min-w-[92px]">
      <div className={`text-xl font-bold tabular-nums ${toneCls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

export function PublishedSummary({ result, onUndo, undoing, onViewMenu }) {
  return (
    <div className="rounded-2xl border bg-gradient-to-br from-emerald-500/15 via-card to-card p-6" data-testid="publish-done">
      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold">
        <CheckCircle2 className="h-5 w-5" />Your menu is live
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {[
          result.created.items ? `${result.created.items} new item${result.created.items === 1 ? '' : 's'}` : null,
          result.updated.items ? `${result.updated.items} updated` : null,
          result.skipped ? `${result.skipped} skipped` : null,
          result.created.categories ? `${result.created.categories} new categor${result.created.categories === 1 ? 'y' : 'ies'}` : null,
          result.photos ? `${result.photos} dish photo${result.photos === 1 ? '' : 's'}` : null,
        ].filter(Boolean).join(' · ') || 'Nothing changed'}. Online ordering, the kiosk and the POS show the menu now.
      </p>
      {(result.warnings || []).length ? (
        <ul className="mt-2 list-disc pl-5 text-sm text-amber-700 dark:text-amber-300">{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onViewMenu} data-testid="view-menu">View menu</Button>
        <Button variant="outline" onClick={onUndo} disabled={undoing} data-testid="undo-import">
          {undoing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}Undo this import
        </Button>
      </div>
    </div>
  );
}
